"use client";

import React from "react";
import { ShieldCheck, ClipboardCheck, HardHat, FileCheck2, CircleCheck as CheckCircle2, Phone, ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function SafetyTeamPhoto() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="w-full h-full bg-muted animate-pulse" />;
  }
  const matchedPhoto = posts.find(p => p.title === "Safety Accreditations Team PPE Image" || p.customFields?.photo_name === "Safety Accreditations Team PPE Image");
  const imgSrc = matchedPhoto?.customFields?.image || matchedPhoto?.featuredImage;
  const imgAlt = matchedPhoto?.customFields?.alt_text || "BMF scaffolding team on active commercial site with complete safety harnesses and PPE";
  if (imgSrc) {
    return <img src={imgSrc} data-wvc-srcset="" data-wvc-sizes="" alt={imgAlt} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-[transform] duration-500" />;
  }
  return <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/scaffolders_working_on_site_with_full_ppe.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/scaffolders_working_on_site_with_full_ppe.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/scaffolders_working_on_site_with_full_ppe.png-wvc-sizes" alt="BMF scaffolding team on active commercial site with complete safety harnesses and PPE" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-[transform] duration-500" />;
}
export default function Safety() {
  const shouldReduceMotion = useReducedMotion();
  const sitePhotoQuery = new WP_Query({
    post_type: "site_photo",
    per_page: 20
  });
  const standards = [{
    id: "tg20",
    title: "TG20:21 Compliance",
    description: "Every tube-and-fitting structure strictly follows NASC TG20:21 operational criteria, complete with full design calculations or certified compliance sheets before handover.",
    badge: "Design Standards",
    icon: "FileCheck2"
  }, {
    id: "sg4",
    title: "SG4:22 Collective Fall Prevention",
    description: "Our scaffolders operate under SG4:22 guidelines, employing advance guardrails, step-up scaffolds, and 100% dual-lanyard harness tie-off at all working heights.",
    badge: "Fall Protection",
    icon: "ShieldCheck"
  }, {
    id: "ppe",
    title: "100% Full Site PPE Mandate",
    description: "Hard hats, high-visibility clothing, steel toecap safety boots, protective gloves, and safety eyewear are mandatory standard issue for every team member on site.",
    badge: "Workplace Protocol",
    icon: "HardHat"
  }, {
    id: "inspections",
    title: "Weekly Statutory Scaffold Inspections",
    description: "Routine 7-day statutory inspections and post-adverse-weather checks logged directly on handover certificates and visible on-site Scafftag systems.",
    badge: "Compliance Checks",
    icon: "ClipboardCheck"
  }];
  const safetyChecklist = ["Full site-specific Risk Assessment & Method Statement (RAMS)", "NASC & CHAS confirmed health & safety accreditations", "Handover inspection handover certificates issued prior to trade access", "Direct oversight by Pete Wood with 20+ years frontline trade leadership"];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 18
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="our-safety-commitment" data-nav="light" className="bg-background py-16 md:py-24 lg:py-28 border-b border-border transition-colors">
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="h-0.5 w-6 bg-primary" />
            <span className="text-xs md:text-sm font-bold tracking-wider uppercase text-primary">
              Safety & Compliance
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default leading-tight">
            Zero-compromise safety on every site
          </h1>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            BMF Scaffolding operates under an uncompromising safety philosophy.
            From certified engineering compliance to weekly statutory sign-offs,
            every structure is erected to safeguard your workforce, property,
            and project schedule.
          </p>
        </div>

        {/* 2-Column Split: Content / Image */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column: Core Safety Standards Matrix */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {standards.map((std, i) => <motion.div key={std.id} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border bg-card rounded-lg hover:border-primary/50 transition-[border-color,box-shadow] duration-200 shadow-xs">
                  <CardContent className="p-6 flex flex-col justify-between h-full">
                    <div>
                      <div className="flex items-center justify-between gap-3 mb-4">
                        <div className="size-10 rounded-md bg-accent flex items-center justify-center text-primary">
                          {std.icon === "FileCheck2" && <FileCheck2 className="size-5" />}
                          {std.icon === "ShieldCheck" && <ShieldCheck className="size-5" />}
                          {std.icon === "HardHat" && <HardHat className="size-5" />}
                          {std.icon === "ClipboardCheck" && <ClipboardCheck className="size-5" />}
                        </div>
                        <span className="text-xs font-semibold px-2.5 py-1 rounded bg-muted text-muted-foreground">
                          {std.badge}
                        </span>
                      </div>
                      <h3 className="text-base md:text-lg font-bold text-foreground font-default mb-2">
                        {std.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-normal">
                        {std.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </motion.div>

          {/* Right Column: Site Image & Compliance Details */}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.45,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-5 flex flex-col gap-6">
            {/* Visual Frame */}
            <div className="relative rounded-lg overflow-hidden border border-border bg-muted shadow-sm aspect-[4/3] group">
              <PostsProvider className="w-full h-full" wp_query={{
              post_type: "site_photo",
              per_page: 20,
              status: ["publish"]
            }}>
                <SafetyTeamPhoto />
              </PostsProvider>
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-secondary/20 to-transparent flex items-end p-5 pointer-events-none">
                <div className="text-secondary-foreground">
                  <p className="text-xs font-bold tracking-wider uppercase text-primary">
                    Active Site Standards
                  </p>
                  <p className="text-sm font-semibold">
                    100% harness attachment and complete protective gear on every project
                  </p>
                </div>
              </div>
            </div>

            {/* Checklist Box */}
            <div className="rounded-lg bg-card border border-border p-6 shadow-xs">
              <h4 className="text-base font-bold text-foreground font-default mb-4 flex items-center gap-2">
                <ShieldCheck className="size-5 text-primary" />
                Our Safety Assurance Checklist
              </h4>
              <ul className="space-y-3">
                {safetyChecklist.map((item, idx) => <li key={idx} data-index={idx} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                    <CheckCircle2 className="size-4 text-chart-4 shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>)}
              </ul>
            </div>
          </motion.div>
        </div>

        {/* Action / Contact Strip */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1],
        delay: 0.15
      }} className="mt-12 lg:mt-16 rounded-lg bg-secondary text-secondary-foreground p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border border-secondary">
          <div className="max-w-2xl">
            <h3 className="text-xl sm:text-2xl font-bold font-default text-secondary-foreground mb-1">
              Need technical RAMS or scaffolding design checks?
            </h3>
            <p className="text-sm sm:text-base text-secondary-foreground/80">
              Speak directly with our operational management team for pre-tender safety packages and compliance verifications.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0 w-full md:w-auto">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-11 px-6 shadow-xs w-full sm:w-auto">
              <Link to="/ContactRequestQuotePage">
                Request a Quote
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="bg-secondary text-secondary-foreground border-secondary-foreground/20 hover:bg-secondary-foreground/10 hover:text-secondary-foreground font-medium h-11 px-5 w-full sm:w-auto">
              <Link to="/ContactRequestQuotePage">
                <Phone className="size-4 mr-1.5 text-primary" />
                Discuss a Project
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}